import { useState, useEffect } from 'react';
import {
  Box,
  CssBaseline,
  CircularProgress,
  Typography,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  InputBase,
  alpha,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { signOut } from 'firebase/auth';
import { auth } from '../../services/firebase';
import Sidebar from '../shared/Sidebar';
import Header from '../shared/Header';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH, HEADER_HEIGHT } from '../../constants/layout';

const AuthLayout = () => {
  const { currentUser, userProfile, loading } = useAuth();
  const { isOpen } = useSidebar();
  const [isLoading, setIsLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate current sidebar width based on state
  const currentSidebarWidth = isMobile ? 0 : (isOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH);

  useEffect(() => {
    // Additional loading check
    const timer = setTimeout(() => {
      setIsLoading(loading);
    }, 2000);

    return () => clearTimeout(timer);
  }, [loading]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Loading state
  if (isLoading) {
    return (
      <Container
        maxWidth="xs"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Authenticating...
        </Typography>
      </Container>
    );
  }

  // Authentication check
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Email verification check:
  // Only require email verification if the user actually has an email and is not using Google/SSO or phone-only auth
  const userEmail = currentUser?.email || auth.currentUser?.email;
  const isGoogleOrSSO = currentUser?.providerData?.some(p =>
    ['google.com', 'github.com', 'apple.com', 'microsoft.com'].includes(p.providerId)
  );
  const isPhoneOnlyUser = !userEmail && (
    currentUser?.providerData?.some(p => p.providerId === 'phone') ||
    Boolean(currentUser?.phoneNumber || auth.currentUser?.phoneNumber)
  );
  const isVerified = Boolean(currentUser?.emailVerified || auth.currentUser?.emailVerified);

  if (userEmail && !isVerified && !isGoogleOrSSO && !isPhoneOnlyUser) {
    return <Navigate to="/verify-email" replace />;
  }

  const displayName = userProfile?.displayName ||
    currentUser?.displayName ||
    (userEmail ? userEmail.split('@')[0] : (currentUser?.phoneNumber || 'User'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#0a0e17' }}>
      <CssBaseline />

      {/* Shared Auth Header */}
      <Header />

      <Box sx={{ display: 'flex', flexGrow: 1, position: 'relative' }}>
        {/* Sidebar */}
        <Sidebar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />

        {/* Main content area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            marginLeft: { xs: 0, md: `${currentSidebarWidth}px` },
            marginTop: 0, // Header is already in flow above
            minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
            backgroundColor: '#0a0e17',
            width: { xs: '100%', md: `calc(100% - ${currentSidebarWidth}px)` },
            padding: 0,
            maxWidth: '100%',
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLayout;
